import React, { useCallback, useRef, useState } from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'

export interface DropzoneProps extends Omit<BoxProps, 'onClick' | 'onDrop' | 'title'> {
  /** Fires with the selected/dropped files, from either browse or drag-drop. */
  onFilesSelected: (files: File[]) => void
  icon?: React.ReactNode
  title?: React.ReactNode
  subtitle?: React.ReactNode
  /** Small mono caption under the copy, e.g. "Always full resolution · Never compressed" */
  hint?: React.ReactNode
  accept?: string
  multiple?: boolean
  disabled?: boolean
}

const Root = styled(Box, {
  shouldForwardProp: (p) => p !== '$active' && p !== '$disabled',
})<{ $active?: boolean; $disabled?: boolean }>(({ $active, $disabled }) => ({
  border: `1.5px dashed ${$active ? 'var(--ds-accent-deep-b, var(--ds-ink))' : 'var(--ds-border-strong)'}`,
  borderRadius: 'var(--ds-radius-lg)',
  padding: '34px 24px',
  textAlign: 'center',
  cursor: $disabled ? 'not-allowed' : 'pointer',
  opacity: $disabled ? 0.5 : 1,
  background: $active ? 'rgba(190,157,253,0.04)' : 'transparent',
  transition: 'border-color var(--ds-duration-normal) var(--ds-ease), background var(--ds-duration-normal) var(--ds-ease)',
  ...(!$disabled && {
    '&:hover': {
      borderColor: 'var(--ds-accent-deep-b, var(--ds-ink))',
      background: 'rgba(190,157,253,0.04)',
    },
  }),
}))

const IconWrap = styled(Box)({
  width: 44,
  height: 44,
  margin: '0 auto 12px',
  borderRadius: 13,
  background: 'var(--ds-gradient-spectrum-soft, var(--ds-hover))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 19,
})

const Title = styled('div')({
  fontSize: 14,
  fontWeight: 650,
  color: 'var(--ds-ink)',
})

const Subtitle = styled('div')({
  fontSize: 12,
  color: 'var(--ds-ink-subtle)',
  marginTop: 4,
})

const Hint = styled('div')({
  marginTop: 12,
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  fontWeight: 600,
  color: 'var(--ds-accent-deep-b, var(--ds-ink-muted))',
})

const HiddenInput = styled('input')({
  position: 'absolute',
  width: 1,
  height: 1,
  opacity: 0,
  overflow: 'hidden',
  pointerEvents: 'none',
})

/** Drag-and-drop / click-to-browse upload target. */
export function Dropzone({
  onFilesSelected,
  icon = '🖼',
  title = 'Drop photos here, or click to browse',
  subtitle,
  hint,
  accept = 'image/*,.heic,.heif',
  multiple = true,
  disabled,
  ...rest
}: DropzoneProps) {
  const [active, setActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return
      onFilesSelected(Array.from(fileList))
    },
    [onFilesSelected],
  )

  return (
    <Root
      $active={active}
      $disabled={disabled}
      role='button'
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) inputRef.current?.click()
      }}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setActive(true)
      }}
      onDragLeave={() => setActive(false)}
      onDrop={(e) => {
        e.preventDefault()
        setActive(false)
        if (!disabled) handleFiles(e.dataTransfer.files)
      }}
      {...rest}
    >
      <IconWrap aria-hidden>{icon}</IconWrap>
      <Title>{title}</Title>
      {subtitle && <Subtitle>{subtitle}</Subtitle>}
      {hint && <Hint>{hint}</Hint>}
      <HiddenInput
        ref={inputRef}
        type='file'
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={(e) => {
          handleFiles(e.target.files)
          e.target.value = ''
        }}
      />
    </Root>
  )
}
